import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Login.css';
import { userService, auth } from '../services';
import { canSignIn, getHomePathForRole } from '../utils/roles';
import ThemeToggle from './ThemeToggle';

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.8 21.8 0 0 1 5.06-6.94" />
    <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.77 21.77 0 0 1-2.16 3.19" />
    <path d="M1 1l22 22" />
    <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const StatStudentsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const StatCoursesIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const StatSuccessIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [otpStage, setOtpStage] = useState(false);

  const handleSuccessfulLogin = (result) => {
    const user = result.user;
    if (!user) {
      setError('Login response missing user data. Please try again.');
      setLoading(false);
      return;
    }

    const userRole = user.role?.toLowerCase();

    if (canSignIn(userRole)) {
      const redirectPath = getHomePathForRole(userRole);
      setSuccessMessage('Login successful. Redirecting...');
      setTimeout(() => {
        if (!auth.isAuthenticated()) {
          setError('Failed to save login data. Please try again.');
          setLoading(false);
          return;
        }
        navigate(redirectPath, { replace: true });
      }, 200);
    } else {
      setError(`Access denied. Staff accounts only. (Role: ${user.role || 'unknown'})`);
      userService.logout();
      setSuccessMessage('');
      setLoading(false);
    }
  };

  const resetOtpStage = () => {
    setOtpStage(false);
    setOtp('');
    setError('');
    setSuccessMessage('');
    setResending(false);
    userService.logout();
  };

  const handleResendOtp = async () => {
    setError('');
    try {
      setResending(true);
      const result = await userService.requestOtp({ email });
      setSuccessMessage(result.message || 'A new verification code has been sent to your email.');
    } catch (err) {
      setError(err?.error || err?.message || 'Could not resend the verification code.');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (otpStage) {
      if (!otp) {
        setError('Please enter the OTP sent to your email.');
        return;
      }

      try {
        setLoading(true);
        const result = await userService.verifyOtp({ email, password, otp });
        handleSuccessfulLogin(result);
      } catch (err) {
        setError(err?.error || err?.message || 'OTP verification failed. Please try again.');
        setLoading(false);
      }
      return;
    }

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    try {
      setLoading(true);
      userService.logout();
      const result = await userService.login({ email, password });

      if (result?.user || result?.token) {
        userService.logout();
      }

      setOtpStage(true);
      setSuccessMessage(result?.message || 'A verification code has been sent to your email.');
      setLoading(false);
    } catch (err) {
      setError(err?.error || err?.message || 'Login failed. Please check your credentials and try again.');
      setLoading(false);
    }
  };

  return (
    <main className="login-page">
      <aside className="login-showcase" aria-label="Fabritech Training Center">
        <div className="login-showcase__media">
          <img
            src="/student.png"
            alt="Student learning on a laptop"
            className="login-showcase__student"
          />
          <div className="login-showcase__overlay" aria-hidden="true" />
          <div className="login-showcase__pattern" aria-hidden="true" />
        </div>

        <div className="login-showcase__inner">
          <header className="login-showcase__brand">
            <img src="/logo.png" alt="Fabritech logo" className="login-showcase__logo" />
            <span>Training Center</span>
          </header>

          <div className="login-showcase__headline">
            <p className="login-showcase__eyebrow">Staff portal</p>
            <h1>
              Train with
              <strong> purpose.</strong>
            </h1>
            <span className="login-showcase__line" aria-hidden="true" />
            <p className="login-showcase__tagline">
              A secure workspace for Fabritech staff to manage students, courses, attendance, and finance.
            </p>
          </div>

          <ul className="login-stats" aria-label="Training center highlights">
            <li className="login-stat">
              <span className="login-stat__icon"><StatStudentsIcon /></span>
              <div>
                <strong>1,200+</strong>
                <small>Students trained</small>
              </div>
            </li>
            <li className="login-stat">
              <span className="login-stat__icon"><StatCoursesIcon /></span>
              <div>
                <strong>25+</strong>
                <small>Professional courses</small>
              </div>
            </li>
            <li className="login-stat">
              <span className="login-stat__icon"><StatSuccessIcon /></span>
              <div>
                <strong>98%</strong>
                <small>Completion rate</small>
              </div>
            </li>
          </ul>
        </div>
      </aside>

      <section className="login-portal login-portal--signin" aria-label="Staff sign in">
        <header className="login-portal__bar">
          <div className="login-portal__brand">
            <img src="/logo.png" alt="Fabritech logo" className="login-logo" />
            <div>
              <div className="login-brand-name">Fabritech</div>
              <div className="login-brand-tag">Training Center</div>
            </div>
          </div>
          <ThemeToggle className="theme-toggle--portal" showLabels />
        </header>

        <div className="login-portal__body">
          <div className="login-signin">
            <div className="login-card__heading">
              <span className="login-badge">Staff portal</span>
              <h2 className="login-title">{otpStage ? 'Verify identity' : 'Welcome back'}</h2>
              <p className="login-subtitle">
                {otpStage
                  ? `Enter the 6-digit code sent to ${email || 'your email'}.`
                  : 'Sign in with your work email to continue.'}
              </p>
            </div>

            {error && (
              <div className="login-alert login-alert--error" role="alert">{error}</div>
            )}
            {successMessage && (
              <div className="login-alert login-alert--success" role="status">{successMessage}</div>
            )}

            <form className="login-form login-form--signin" onSubmit={handleSubmit} noValidate>
              {!otpStage && (
                <>
                  <div className="login-field">
                    <label htmlFor="email">Work email</label>
                    <div className="login-input-wrap">
                      <span className="login-input-icon"><MailIcon /></span>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="nina.v@example.com"
                        disabled={loading}
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>

                  <div className="login-field">
                    <div className="login-field__top">
                      <label htmlFor="password">Password</label>
                      <a href="mailto:info@fabritech.rw?subject=Password%20assistance">Forgot password?</a>
                    </div>
                    <div className="login-password-wrap">
                      <span className="login-input-icon"><LockIcon /></span>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        disabled={loading}
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        className="login-password-toggle"
                        onClick={() => setShowPassword((v) => !v)}
                        disabled={loading}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {otpStage && (
                <>
                  <div className="login-field">
                    <div className="login-field__top">
                      <label htmlFor="otp">Verification code</label>
                      <button type="button" className="login-text-btn" onClick={resetOtpStage} disabled={loading || resending}>
                        Use a different account
                      </button>
                    </div>
                    <div className="login-input-wrap">
                      <span className="login-input-icon"><LockIcon /></span>
                      <input
                        type="text"
                        id="otp"
                        className="login-otp-input"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        disabled={loading}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        required
                      />
                    </div>
                  </div>
                  <p className="login-otp-help">
                    Didn&apos;t receive the email?
                    <button type="button" className="login-text-btn" onClick={handleResendOtp} disabled={loading || resending}>
                      {resending ? 'Sending…' : 'Resend code'}
                    </button>
                  </p>
                </>
              )}

              <button type="submit" className="login-submit" disabled={loading}>
                {loading ? (otpStage ? 'Verifying…' : 'Signing in…') : (otpStage ? 'Verify code' : 'Sign in')}
                {loading ? <span className="login-spinner" aria-hidden="true" /> : <span className="login-submit__icon"><ArrowIcon /></span>}
              </button>
            </form>

            {!otpStage && (
              <div className="login-alt">
                <p>
                  New staff member? <Link to="/register">Request an account</Link>
                </p>
                <p>
                  Looking to enroll? <Link to="/">Student registration</Link>
                </p>
              </div>
            )}
          </div>
        </div>

        <footer className="login-portal__foot">
          <span><ShieldIcon /> Encrypted staff access</span>
          <a href="mailto:info@fabritech.rw">Need help?</a>
        </footer>
      </section>
    </main>
  );
};

export default Login;
